import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Download, RefreshCw, Monitor, Smartphone, Eraser, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import { api, API, streamPost, formatError } from "@/lib/api";
import { EstimateDialog } from "@/components/workspace/EstimateDialog";
import { AssistantWidget } from "@/components/AssistantWidget";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { AppPreview, storageKey } from "@/components/AppPreview";
import { ChatPanel } from "@/components/workspace/ChatPanel";
import { FilesView, QualityView, VersionsView, SpecView, Empty } from "@/components/workspace/Panels";
import { PublishView } from "@/components/workspace/PublishView";
import { SubmissionsView } from "@/components/workspace/SubmissionsView";
import { BotWorkspace } from "@/components/workspace/BotWorkspace";

const TABS = [["preview", "Vista previa"], ["files", "Archivos"], ["quality", "Calidad"], ["spec", "Especificación"], ["versions", "Versiones"], ["forms", "Formularios"], ["publish", "Publicar"]];
const autoSent = new Set();

function useProject(id) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/projects/${id}`).then((r) => { setData(r.data); return r.data; }), [id]);
  useEffect(() => { load().catch(() => setData(false)); }, [load]);
  return [data, load, setData];
}

function PreviewToolbar({ device, setDevice, onReload, onReset, viewing, current, onBack }) {
  return (
    <div className="h-11 flex items-center justify-between px-3 border-b border-white/5 bg-[#0c1019]">
      <div className="flex items-center gap-1">
        <button onClick={() => setDevice("desktop")} data-testid="preview-desktop-btn" className={`p-1.5 rounded ${device === "desktop" ? "text-white bg-white/10" : "text-slate-500"}`}><Monitor className="w-4 h-4" /></button>
        <button onClick={() => setDevice("mobile")} data-testid="preview-mobile-btn" className={`p-1.5 rounded ${device === "mobile" ? "text-white bg-white/10" : "text-slate-500"}`}><Smartphone className="w-4 h-4" /></button>
        <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-emerald-300/80"><Shield className="w-3 h-3" />Entorno aislado · sin red</span>
      </div>
      {viewing !== current && <button onClick={onBack} className="text-xs text-amber-300" data-testid="preview-back-current-btn">Viendo v{viewing} · volver a la actual</button>}
      <div className="flex items-center gap-1">
        <button onClick={onReset} title="Borrar datos de prueba" data-testid="preview-reset-data-btn" className="p-1.5 rounded text-slate-400 hover:text-white"><Eraser className="w-4 h-4" /></button>
        <button onClick={onReload} title="Recargar" data-testid="preview-reload-btn" className="p-1.5 rounded text-slate-400 hover:text-white"><RefreshCw className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function Workspace() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [data, load, setData] = useProject(id);
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("github") ? "publish" : "preview"));
  const [input, setInput] = useState("");
  const [live, setLive] = useState(null);
  const [viewing, setViewing] = useState(0);
  const [version, setVersion] = useState(null);
  const [nonce, setNonce] = useState(0);
  const [device, setDevice] = useState("desktop");
  const [meta, setMeta] = useState({});
  const [pending, setPending] = useState(null);
  const reported = useRef(new Set());

  useEffect(() => { api.get("/agents").then((r) => setMeta(r.data)); }, []);
  useEffect(() => { if (data?.project) setViewing((v) => v || data.project.current_version); }, [data]);
  useEffect(() => {
    if (!viewing) return setVersion(null);
    api.get(`/projects/${id}/versions/${viewing}`).then((r) => setVersion(r.data));
  }, [id, viewing]);

  const requestSend = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || live) return;
    try {
      const { data: est } = await api.post(`/projects/${id}/estimate`, { message: msg });
      setPending({ msg, est });
    } catch (e) { toast.error(formatError(e)); }
  }, [id, input, live]);

  const send = useCallback(async (msg, maxCredits) => {
    setPending(null);
    setInput("");
    setData((d) => ({ ...d, messages: [...d.messages, { id: `tmp-${Date.now()}`, role: "user", content: msg }] }));
    setLive({ agents: {}, chars: 0, text: "" });
    let raw = "";
    try {
      await streamPost(`/projects/${id}/chat`, { message: msg, max_credits: maxCredits }, (ev) => {
        if (ev.type === "agent") setLive((l) => ({ ...l, agents: { ...l.agents, [ev.agent]: ev } }));
        if (ev.type === "delta") {
          raw += ev.content;
          const cut = raw.indexOf("=== FILE:");
          setLive((l) => ({ ...l, chars: raw.length, text: (cut >= 0 ? raw.slice(0, cut) : raw).trim() }));
        }
        if (ev.type === "error") toast.error(ev.message);
        if (ev.type === "done") {
          if (ev.version) { setViewing(ev.version); setTab("preview"); }
          toast.success(`${ev.version ? `Versión v${ev.version} lista. ` : ""}Se cobraron ${ev.charged} créditos (estimado ${ev.estimated})`);
        }
      });
    } catch (e) {
      toast.error(e.message || formatError(e));
    }
    setLive(null);
    await load();
    refreshUser();
  }, [id, load, refreshUser, setData]);

  const jobRunning = !!data?.project?.job && !live;
  useEffect(() => {
    if (!jobRunning) return;
    const t = setInterval(() => { load().then((d) => { if (!d.project.job) { refreshUser(); setViewing(d.project.current_version); } }); }, 5000);
    return () => clearInterval(t);
  }, [jobRunning, load, refreshUser]);

  useEffect(() => {
    const initial = location.state?.initialPrompt;
    if (!data?.project || !user || !initial || autoSent.has(id)) return;
    autoSent.add(id);
    navigate(location.pathname, { replace: true, state: null });
    if (data.messages.length) return;
    setInput(initial);
    if (user.credits > 0) requestSend(initial);
  }, [data, user, id, location, navigate, requestSend]);

  const onRuntime = useCallback(async (errors) => {
    if (!version || reported.current.has(version.number)) return;
    reported.current.add(version.number);
    const { data: rt } = await api.post(`/projects/${id}/versions/${version.number}/runtime`, { errors });
    setVersion((v) => (v && v.number === version.number ? { ...v, runtime: rt } : v));
    if (errors.length) toast.warning(`El verificador detectó ${errors.length} error(es) de ejecución. Revisa la pestaña Calidad.`);
  }, [id, version]);

  const restore = async (n) => {
    try { const { data: r } = await api.post(`/projects/${id}/versions/${n}/restore`); await load(); setViewing(r.number); toast.success(`Restaurada como v${r.number}`); }
    catch (e) { toast.error(formatError(e)); }
  };

  if (data === false) return <Empty>Proyecto no encontrado. <Link to="/app" className="text-cyan-400 ml-1">Volver</Link></Empty>;
  if (!data) return <div className="h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  const p = data.project;
  if (p.type === "chatbot") return <BotWorkspace project={p} />;

  return (
    <div className="h-screen flex flex-col bg-[#0A0D14]" data-testid="workspace-page">
      <header className="h-14 shrink-0 flex items-center justify-between gap-3 px-4 border-b border-white/5 bg-[#0c1019]">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/app" className="p-1.5 rounded-lg text-slate-400 hover:text-white" data-testid="workspace-back-btn"><ArrowLeft className="w-4 h-4" /></Link>
          <Logo to="/app" small />
          <h1 className="text-sm font-medium text-white truncate" data-testid="workspace-project-name">{p.name}</h1>
          <span className="hidden sm:inline font-mono text-xs text-slate-500" data-testid="workspace-version">v{p.current_version}</span>
          <span className="hidden sm:inline text-[10px] px-2 py-0.5 rounded-full border border-[#222B40] text-slate-400" data-testid="workspace-project-type">{p.type === "website" ? "Página web" : "Aplicación"}</span>
          <span className="hidden md:inline text-[11px] text-slate-500">· {meta.model?.model}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <AssistantWidget inline />
          <a href={p.current_version ? `${API}/projects/${id}/export` : undefined} data-testid="workspace-export-btn"
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[#222B40] ${p.current_version ? "text-slate-300 hover:text-white" : "text-slate-600 pointer-events-none"}`}>
            <Download className="w-3.5 h-3.5" />Exportar
          </a>
          <button onClick={() => setTab("publish")} disabled={!p.current_version} data-testid="workspace-publish-btn" className="text-xs px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-400 disabled:opacity-40 text-white">{p.published ? "Publicada" : "Publicar"}</button>
        </div>
      </header>
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        <aside className="lg:w-[400px] shrink-0 h-[45vh] lg:h-auto border-b lg:border-b-0 lg:border-r border-white/5 bg-[#0c1019]">
          {jobRunning && <div className="m-3 text-xs text-cyan-200 bg-cyan-500/10 border border-cyan-500/30 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="job-running-banner"><Loader2 className="w-3.5 h-3.5 animate-spin" />Los agentes siguen trabajando en este proyecto…</div>}
          <ChatPanel messages={data.messages} live={live} input={input} setInput={setInput} onSend={() => requestSend()} running={!!live || jobRunning}
            credits={user.credits} />
        </aside>
        <section className="flex-1 min-w-0 min-h-0 flex flex-col">
          <div className="flex gap-1 px-3 pt-2 border-b border-white/5 overflow-x-auto scroll-thin">
            {TABS.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`workspace-tab-${k}`}
                className={`px-3 py-2 text-xs whitespace-nowrap border-b-2 transition-colors ${tab === k ? "border-cyan-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <div className="flex-1 min-h-0">
            {tab === "preview" && (
              <div className="h-full flex flex-col">
                <PreviewToolbar device={device} setDevice={setDevice} viewing={viewing} current={p.current_version} onBack={() => setViewing(p.current_version)}
                  onReload={() => setNonce((n) => n + 1)} onReset={() => { localStorage.removeItem(storageKey(id)); setNonce((n) => n + 1); toast.success("Datos de prueba borrados"); }} />
                <div className="flex-1 min-h-0 bg-[#05070c] grid place-items-center p-3">
                  {version?.html ? (
                    <div className={`h-full ${device === "mobile" ? "w-[390px] max-w-full" : "w-full"} rounded-xl overflow-hidden border border-[#222B40]`}>
                      <AppPreview html={version.html} dataKey={id} nonce={nonce} onRuntime={version.number === p.current_version ? onRuntime : undefined} onForm={(f) => api.post(`/projects/${id}/submissions/test`, { form: f.form, data: f.data }).then(() => toast.success(`Formulario «${f.form}» recibido (prueba). Revísalo en la pestaña Formularios.`))} />
                    </div>
                  ) : <Empty>{live ? "Los agentes están construyendo tu aplicación…" : "La vista previa aparecerá aquí cuando los agentes generen la primera versión."}</Empty>}
                </div>
              </div>
            )}
            {tab === "files" && <FilesView files={version?.files} />}
            {tab === "quality" && <QualityView version={version} onFix={(errs) => requestSend(`Corrige estos errores detectados por el verificador:\n- ${errs.slice(0, 10).join("\n- ")}`)} />}
            {tab === "spec" && <SpecView spec={p.spec} />}
            {tab === "versions" && <VersionsView versions={data.versions} current={p.current_version} viewing={viewing} onView={(n) => { setViewing(n); setTab("preview"); }} onRestore={restore} />}
            {tab === "forms" && <SubmissionsView projectId={id} />}
            {tab === "publish" && <PublishView project={p} versions={data.versions} onChanged={load} onPreview={(n) => { setViewing(n); setTab("preview"); }} />}
          </div>
        </section>
      </div>
      <EstimateDialog pending={pending} onCancel={() => setPending(null)} onConfirm={send} />
    </div>
  );
}
